import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTRPCAPI, useTRPCQuery } from '../../../../trpc/trpc';
import { useRealtimeEvent } from '../../../realtime/useRealtimeEvent';
import { useRealtimePollInterval } from '../../../realtime/useRealtimePollInterval';
import type { InboxPage } from '../outreachTypes';

/** The poll of the count while the socket is down. */
export const OUTREACH_INBOX_BADGE_POLL_MS = 2 * 60 * 1000;

/**
 * The Inbox badge (navigation v2: the red "Needs you" count of the Inbox menu entry): the replies in
 * the group `needsYou` (`counts.needsYou` = `counts.byGroup.needsYou`). It reads `outreach.inbox.list`
 * with the group filter, so the page of rows is small; the counts ignore the filters. A new reply
 * (`outreach.reply.received`) reads it again. `undefined` until loaded, or for no artist (then it
 * calls nothing).
 */
export function useOutreachInboxCount(accountId: number | null): number | undefined {
  const trpcAPI = useTRPCAPI();
  const queryClient = useQueryClient();
  const refetchInterval = useRealtimePollInterval(OUTREACH_INBOX_BADGE_POLL_MS);
  const enabled = accountId !== null;
  const { data } = useTRPCQuery(
    trpcAPI.outreach.inbox.list.queryOptions(
      { accountId: accountId ?? 0, group: 'needsYou' },
      {
        enabled,
        retry: false,
        refetchInterval: !enabled || refetchInterval === 0 ? false : refetchInterval,
        // A background read: a plan refusal (Free) opens no upgrade prompt.
        meta: { skipM24LimitPrompt: true },
      }
    )
  );

  const refresh = useCallback(() => {
    if (!enabled) {
      return;
    }
    queryClient.invalidateQueries({ queryKey: trpcAPI.outreach.inbox.pathKey() }).catch(console.error);
  }, [enabled, queryClient, trpcAPI]);
  useRealtimeEvent('outreach.reply.received', refresh);

  const page = data as InboxPage | undefined;
  return !enabled || page === undefined ? undefined : page.counts.needsYou;
}
